<template>
  <div>
    <v-card class="mb-4 elevation-1">
      <v-card-title>
        {{ academyName }}
        <v-spacer />
        <v-btn v-if="isAdmin" color="primary" dark @click="showEditAcademyDialog">
          Edit Academy
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col v-if="academyDetails.logo" cols="12" md="6">
            <strong>Logo:</strong>
            <div class="mt-2">
              <v-img-ex
                :src="academyDetails.logo"
                max-width="200"
                max-height="100"
                style="cursor:pointer"
                @click.native="openImageOverlay(academyDetails.logo)"
              />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Address:</strong>
            <div>{{ academyDetails.address?.addressLine || 'N/A' }}</div>
            <div>{{ academyDetails.address?.city || 'N/A' }}</div>
            <div>{{ academyDetails.address?.country || 'N/A' }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Contact:</strong>
            <div><strong>Telephone:</strong> {{ academyDetails.telephone || 'N/A' }}</div>
            <div><strong>Email:</strong> {{ academyDetails.email || 'N/A' }}</div>
          </v-col>
          <v-col cols="12" md="6">
            <strong>Facility Information:</strong>
            <div><strong>Technology Used:</strong> {{ academyDetails.technologyUsed || 'N/A' }}</div>
            <div><strong>Facility Type:</strong> {{ academyDetails.facilityType || 'N/A' }}</div>
            <div><strong>Number of Coaches:</strong> {{ academyDetails.numberOfCoaches || 'N/A' }}</div>
          </v-col>
          <v-col cols="12">
            <strong>Head Coach:</strong>
            <div>{{ academyDetails.headCoach || headCoachEmail || 'None' }}</div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-text>
        <v-row justify="space-between">
          <v-col cols="auto">
            <v-btn v-if="isAdmin || isCoach" color="primary" dark @click="showAddUserDialog">
              Add New User
            </v-btn>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filter"
              :append-icon="icons.mdiMagnify"
              label="Search Users"
              single-line
              hide-details
              dense
              outlined
              @input="filterUsers"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="filteredUsers"
        :loading="isUsersLoading"
        :items-per-page="10"
        class="elevation-1"
      >
        <template #item.userType="{ item }">
          <v-chip
            outlined
            color="primary"
            class="ma-0"
          >
            {{ item.userType }}
          </v-chip>
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex justify-end" style="max-width: 150px">
            <v-icon
              v-if="canRemoveUser(item)"
              @click="removeUser(item)"
            >
              {{ icons.mdiDeleteOutline }}
            </v-icon>
            <span v-else class="text--disabled">
              <!-- No actions available -->
            </span>
          </div>
        </template>
      </v-data-table>

      <academy-edit-user-dialog
        v-model="isEditUserDialogVisible"
        :edit-mode="editMode"
        :item="selectedUser"
        :is-loading="isLoading"
        @search="searchUserByEmail"
        @add="addUser"
        @save="saveUserChanges"
        @cancel="closeDialog"
      />

      <academy-edit-dialog
        v-model="isEditAcademyDialogVisible"
        :item="academyDetails"
        :is-loading="isLoading"
        @save="saveAcademyDetails"
        @cancel="closeEditAcademyDialog"
        @relations-updated="loadAcademyContentPacks"
      />
    </v-card>

    <v-card class="mt-4">
      <v-card-title>
        Content Packs
      </v-card-title>
      <v-card-text class="py-2">
        <v-list v-if="academyContentPacks.length" dense class="py-0">
          <v-list-item class="academy-content-pack-header px-0">
            <v-list-item-content>
              <v-list-item-title class="text-caption font-weight-bold">
                Content Pack
              </v-list-item-title>
            </v-list-item-content>
            <div class="academy-content-pack-purchased text-caption font-weight-bold">
              Purchased
            </div>
            <v-list-item-action v-if="isAdmin" />
          </v-list-item>
          <v-list-item v-for="contentPack in academyContentPacks" :key="contentPack.id">
            <v-list-item-content>
              <v-list-item-title>{{ contentPack.name }}</v-list-item-title>
            </v-list-item-content>
            <div class="academy-content-pack-purchased text-body-2">
              {{ formatPurchased(contentPack.purchased) }}
            </div>
            <v-list-item-action v-if="isAdmin">
              <v-btn icon small @click="removeContentPack(contentPack.id)">
                <v-icon small>
                  {{ icons.mdiDeleteOutline }}
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <div v-else class="text-body-2 grey--text">
          No content packs assigned.
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mdiMagnify, mdiPencilOutline, mdiDeleteOutline } from '@mdi/js'
import { ref, computed, onMounted } from 'vue'
import useAcademies from '@/composables/api/academies'
import useContentPackAcademies from '@/composables/api/contentPackAcademies'
import useAuth from '@/composables/useAuth'
import { useRouter } from '@/utils/index'
import AcademyEditDialog from '@/views/academies/partials/AcademyEditDialog.vue'
import VImgEx from '@/components/VImgEx.vue'
import useImageLightBox from '@/composables/imageLightBox'
import AcademyEditUserDialog from './AcademyEditUserDialog.vue'

export default {
  components: {
    AcademyEditUserDialog,
    AcademyEditDialog,
    VImgEx,
  },
  setup() {
    const { openImageOverlay } = useImageLightBox()
    const {
      updateUser,
      fetchAcademyUsers,
      fetchAcademyById,
      searchUserByEmail,
      users,
      isUsersLoading,
      updateAcademy,
      getUserAcademy,
    } = useAcademies()
    const { fetchContentPacksByAcademyId, deleteContentPackAcademy, formatPurchased } = useContentPackAcademies()
    const { hasRole } = useAuth()
    const { route } = useRouter()

    const academyId = ref(route.value.params.id)
    const academyName = ref('')
    const currentUser = ref(null)
    const academyDetails = ref({
      address: {
        addressLine: '',
        city: '',
        country: '',
      },
      telephone: '',
      email: '',
      technologyUsed: '',
      facilityType: '',
      numberOfCoaches: '',
      headCoach: '',
      billingId: '',
      description: '',
      location: '',
      photo: '',
      bulletPoints: [],
    })
    const filter = ref('')
    const filteredUsers = ref([])
    const headCoachEmail = ref(null)
    const isEditUserDialogVisible = ref(false)
    const isEditAcademyDialogVisible = ref(false)
    const selectedUser = ref({})
    const editMode = ref(false)
    const isLoading = ref(false)
    const academyContentPacks = ref([])
    const isAdmin = computed(() => hasRole(['ADMIN']))
    const isCoach = computed(() => hasRole(['COACH']))

    const headers = [
      { text: 'Email', value: 'email' },
      { text: 'User Type', value: 'userType' },
      {
        text: 'Actions', value: 'actions', sortable: false, align: 'end', width: '150px',
      },
    ]

    const filterUsers = () => {
      const search = filter.value.toLowerCase()
      filteredUsers.value = users.value.filter(user => Object.values(user)
        .join(' ')
        .toLowerCase()
        .includes(search))
    }

    const refreshUsers = async () => {
      try {
        await fetchAcademyUsers(academyId.value)
        filteredUsers.value = users.value
        const headCoach = users.value.find(user => user.isHeadCoach)
        headCoachEmail.value = headCoach ? headCoach.email : null
      } catch (error) {
        console.error('Failed to refresh users:', error)
      }
    }

    const fetchCurrentUser = async () => {
      try {
        if (hasRole(['COACH'])) {
          const user = await getUserAcademy()
          currentUser.value = user
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error)
      }
    }

    const canRemoveUser = user => {
      // Admin can remove anyone
      if (isAdmin.value) {
        return true
      }

      // Coach can only remove non-coach users, not other coaches
      if (isCoach.value) {
        // Coach cannot remove other users if they are coaches
        if (user.userType === 'coach' || user.userType === 'COACH') {
          return false
        }

        return true
      }

      return false
    }

    const fetchAcademyDetails = async () => {
      try {
        const academy = await fetchAcademyById(academyId.value)
        if (academy) {
          academyName.value = academy.name
          academyDetails.value = academy
        } else {
          console.error('Academy not found')
        }
      } catch (error) {
        console.error('Failed to fetch academy details:', error)
      }
    }

    const loadAcademyContentPacks = async () => {
      academyContentPacks.value = await fetchContentPacksByAcademyId(academyId.value)
    }

    const showAddUserDialog = () => {
      selectedUser.value = {}
      editMode.value = false
      isEditUserDialogVisible.value = true
    }

    const showEditAcademyDialog = () => {
      isEditAcademyDialogVisible.value = true
    }

    const saveAcademyDetails = async updatedAcademy => {
      isLoading.value = true
      try {
        await updateAcademy(updatedAcademy)
        await fetchAcademyDetails()
        isEditAcademyDialogVisible.value = false
      } catch (error) {
        console.error('Failed to save academy details:', error)
      } finally {
        isLoading.value = false
      }
    }

    const closeEditAcademyDialog = () => {
      isEditAcademyDialogVisible.value = false
    }

    const editUser = user => {
      selectedUser.value = { ...user }
      editMode.value = true
      isEditUserDialogVisible.value = true
    }

    const addUser = async user => {
      try {
        const newUser = {
          userId: user.id,
          companyId: academyId.value,
          isHeadCoach: false,
        }

        const isHeadCoachExists = users.value.some(existingUser => existingUser.isHeadCoach)
        if (!isHeadCoachExists) {
          newUser.isHeadCoach = true
        }

        isLoading.value = true
        await updateUser(newUser)
        await refreshUsers()
        isEditUserDialogVisible.value = false
      } catch (error) {
        console.error('Failed to add user to academy:', error)
      } finally {
        isLoading.value = false
      }
    }

    const removeUser = async user => {
      if (!canRemoveUser(user)) {
        console.warn('User does not have permission to remove this user')

        return
      }

      try {
        const newUser = {
          userId: user.userId,
          companyId: null,
          isHeadCoach: false,
        }

        isLoading.value = true
        await updateUser(newUser)
        await refreshUsers()
        isEditUserDialogVisible.value = false
      } catch (error) {
        console.error('Failed to remove user from academy:', error)
      } finally {
        isLoading.value = false
      }
    }

    const removeContentPack = async contentPackId => {
      try {
        isLoading.value = true
        await deleteContentPackAcademy(academyId.value, contentPackId)
        await loadAcademyContentPacks()
      } catch (error) {
        console.error('Failed to remove content pack from academy:', error)
      } finally {
        isLoading.value = false
      }
    }

    const saveUserChanges = async user => {
      try {
        await updateUser(user)
        await refreshUsers()
        isEditUserDialogVisible.value = false
      } catch (error) {
        console.error('Failed to save user changes:', error)
      }
    }

    const closeDialog = () => {
      isEditUserDialogVisible.value = false
    }

    onMounted(async () => {
      await fetchCurrentUser()
      await fetchAcademyDetails()
      await refreshUsers()
      await loadAcademyContentPacks()
    })

    return {
      filter,
      filteredUsers,
      filterUsers,
      showAddUserDialog,
      showEditAcademyDialog,
      saveAcademyDetails,
      closeEditAcademyDialog,
      editUser,
      isEditUserDialogVisible,
      isEditAcademyDialogVisible,
      selectedUser,
      editMode,
      isLoading,
      headers,
      academyName,
      academyDetails,
      academyContentPacks,
      formatPurchased,
      isAdmin,
      isCoach,
      icons: { mdiMagnify, mdiPencilOutline, mdiDeleteOutline },
      isUsersLoading,
      addUser,
      removeUser,
      removeContentPack,
      saveUserChanges,
      closeDialog,
      headCoachEmail,
      searchUserByEmail,
      canRemoveUser,
      openImageOverlay,
      loadAcademyContentPacks,
    }
  },
}
</script>

<style scoped>
.academy-content-pack-header {
  min-height: 28px;
}

.academy-content-pack-purchased {
  min-width: 72px;
  text-align: center;
}
</style>
